import { datadogRum } from '@datadog/browser-rum';

const metaTagNames = [
  "datadog_rum_application_id",
  "datadog_rum_client_token",
  "env",
  "user_id",
  "user_name",
  "user_email",
  "account_id",
  "application_version"
]

// Query the DOM once for relevant meta tags
const metaTags = document.querySelectorAll(metaTagNames.map((name) => `meta[name=${name}]`).join(", "))
const metaData = new Map(
  Array.from(metaTags).map(meta => [meta.getAttribute('name'), meta.content])
)

const applicationId = metaData.get('datadog_rum_application_id')
const clientToken = metaData.get('datadog_rum_client_token')
const env = metaData.get('env')

const hasEnvConfig = [applicationId, clientToken, env].every((configValue) => configValue?.trim())

const excludedTracingHostnames = [
  't.huntress.io' // Exclude Snowplow tracking endpoint to avoid CORS issues
]

const ignoredRumResourceHostnames = [
  'amplitude.huntress.io',    // Amplitude Metrics
  'brainfi.sh',               // Brainfish Widget
  'canny.io',                 // Customer feedback
  'datadoghq.com',            // Datadog Charts on admin dashboard
  'google-analytics.com',     // Google Analytics
  'googletagmanager.com',     // Google Analytics
  't.huntress.io',            // Snowplow Metrics
  'zdassets.com'              // Zendesk Widget Assets
]

function resourceUrlForRumEvent(event, context) {
  if (event.type !== 'resource') {
    return null
  }

  return event.resource?.url || context?.performanceEntry?.name || null
}

function shouldIgnoreRumEvent(event, context) {
  const resourceUrl = resourceUrlForRumEvent(event, context)

  if (!resourceUrl) {
    return false
  }

  try {
    const hostname = new URL(resourceUrl).hostname
    return ignoredRumResourceHostnames.some((ignoredHostname) => hostname === ignoredHostname || hostname.endsWith(`.${ignoredHostname}`))
  } catch {
    return false
  }
}

if (hasEnvConfig) {
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: 'portal',
    env,
    version: metaData.get('application_version'),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackBfcacheViews: true,
    allowedTracingUrls: [
      (url) => {
        try {
          const parsedUrl = new URL(url);
          const hostname = parsedUrl.hostname;

          if (excludedTracingHostnames.includes(hostname)) {
            return false;
          }

          return hostname.endsWith('.huntress.io');
        } catch {
          return false;
        }
      }
    ],
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ["feature_flags"],
    beforeSend: (event, context) => !shouldIgnoreRumEvent(event, context),
  });

  datadogRum.setUser({
    id: metaData.get('user_id'),
    name: metaData.get('user_name'),
    email: metaData.get('user_email'),
    account_id: metaData.get('account_id'),
  });

  // Define a function that can be called at the bottom of the page to add all
  // our Feature Flag evaluations
  //
  // features: Is an array of tuples (e.g. `[['feature_name', true]]`)
  window.addFeatureFlagEvaluations = (features = []) => {
    try {
      // Map dedupes the keys and properly initializes
      // to empty if features is null
      const featuresMap = new Map(features || []);

      for (let [key, val] of featuresMap) {
        datadogRum.addFeatureFlagEvaluation(key, val)
      }
    } catch (error) {
      console.error("Invalid features format:", error)
    }
  }
}
